import { useState, useEffect } from "react";
import { useListTransactions, useCreateTransaction, useUpdateTransaction, useDeleteTransaction, useListFinancialCategories, getGetFinancialSummaryQueryKey, getListTransactionsQueryKey, getListFinancialCategoriesQueryKey, customFetch } from "@workspace/api-client-react";
import type { Transaction } from "@workspace/api-client-react";
import { formatCurrency } from "@/lib/utils";
import { brDayKey } from "@/lib/datetime";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DataBr } from "@/components/ui/data-br";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { format, parseISO } from "date-fns";
import { EtiquetaNota } from "@/components/admin/NotaDaCompra";
import { ptBR } from "date-fns/locale";
import { ArrowDownRight, ArrowUpRight, Plus, Loader2, Pencil, Trash2, X, MoreVertical, Download, Repeat, AlertTriangle, CheckCircle2, Layers } from "lucide-react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { CurrencyInput } from "@/components/ui/currency-input";
import { PeriodFilter } from "@/components/admin/PeriodFilter";
import { EMPTY_PERIOD_STATE, resolvePeriod, periodFileTag, type PeriodState } from "@/lib/period-filter";
import { useQueryClient, useMutation, useQuery } from "@tanstack/react-query";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { errorTitle, TRY_AGAIN_DESCRIPTION } from "@/lib/messages";

// Formas de pagamento das despesas/contas (livre, mas padronizado no select).
export const EXPENSE_PAYMENT_METHODS = ["Dinheiro", "PIX", "Cartão de crédito", "Cartão de débito", "Boleto", "Transferência", "Outro"];

function localIsoDate(d = new Date()): string {
  return new Intl.DateTimeFormat("en-CA").format(d);
}

export default function Finance() {
  const [isModalOpen, setModalOpen] = useState(false);
  const [editingTx, setEditingTx] = useState<Transaction | null>(null);
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // Período: o MESMO componente e as mesmas opções da página de Pedidos
  // (lib/period-filter). Default: mês atual.
  const [periodState, setPeriodState] = useState<PeriodState>({
    period: "mes",
    ...EMPTY_PERIOD_STATE,
  });

  const range = resolvePeriod(periodState, localIsoDate());

  // NÃO usar useGetFinancialSummary: o hook gerado não tem params (a URL sai
  // sempre sem dateFrom/dateTo e a queryKey é constante) — os cards ficavam
  // presos no mês corrente enquanto o título seguia o filtro. Chamada direta
  // com a queryKey gerada como PREFIXO pra manter as invalidações existentes.
  const { data: summary, isLoading: loadingSummary } = useQuery<any>({
    queryKey: [...getGetFinancialSummaryQueryKey(), range.dateFrom, range.dateTo],
    queryFn: () =>
      customFetch(
        `/api/financial/summary?dateFrom=${encodeURIComponent(range.dateFrom)}&dateTo=${encodeURIComponent(range.dateTo)}`,
      ),
  });
  const { data: txData, isLoading: loadingTx } = useListTransactions(
    { dateFrom: range.dateFrom, dateTo: range.dateTo } as any,
  );

  const [exporting, setExporting] = useState(false);
  const handleExport = async () => {
    setExporting(true);
    try {
      const blob = await customFetch<Blob>(
        `/api/financial/export?dateFrom=${range.dateFrom}&dateTo=${range.dateTo}`,
        { responseType: "blob" } as any,
      );
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `financeiro_${periodFileTag(range)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast({ title: errorTitle("exportar o relatório"), description: TRY_AGAIN_DESCRIPTION, variant: "destructive" });
    } finally {
      setExporting(false);
    }
  };

  // Contas pendentes: consulta global (todas as pendentes, sem data), mas a
  // LISTA respeita o teto do período — parcela de mês futuro só aparece quando
  // a lojista navegar até lá (antes 6x de compra inundava a aba). Filtro pelo
  // TETO (due <= dateTo), não pelo piso: conta VENCIDA é passado e cai sempre
  // dentro do teto, então nunca some — o banner "X vencidas" e a lista batem.
  const { data: pendingData } = useListTransactions({ status: "pendente" } as any);
  const allPendingTxs = ((pendingData as any)?.transactions ?? []) as any[];
  const noPeriodo = (t: any) => (t.dueDate || t.date) <= range.dateTo;
  const pendingTxs = allPendingTxs.filter((t) => t.type !== "receita" && noPeriodo(t));
  const receivableTxs = allPendingTxs.filter((t) => t.type === "receita" && noPeriodo(t));
  const todayIso = localIsoDate();

  const [payingTx, setPayingTx] = useState<any | null>(null);

  // Seções em pills (padrão das categorias do link público): só a ativa aparece.
  const [section, setSection] = useState<"historico" | "contas" | "receber" | "recorrentes">("historico");
  // Criar despesa recorrente pelo menu do botão Adicionar (fora do card da lista).
  const [creatingRecurring, setCreatingRecurring] = useState(false);
  const [creatingInstallments, setCreatingInstallments] = useState(false);

  const deleteTxMutation = useDeleteTransaction({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getListTransactionsQueryKey() });
        queryClient.invalidateQueries({ queryKey: getGetFinancialSummaryQueryKey() });
        toast({ title: "Transação excluída!" });
      },
      onError: () => {
        toast({ title: errorTitle("excluir a transação"), description: TRY_AGAIN_DESCRIPTION, variant: "destructive" });
      },
    },
  });

  // Confirmação de exclusão via AlertDialog (substitui confirm() nativo —
  // mesmo anti-padrão do PR 2). Único state mantém qual transação está
  // pra ser excluída; o dialog renderiza uma vez no fim, não por linha.
  const [deleteConfirmTxId, setDeleteConfirmTxId] = useState<string | null>(null);
  // Excluir a compra parcelada inteira (todas as parcelas pendentes do lote).
  // Alcance da exclusão: `anchor` = vencimento da parcela clicada. "Só as
  // próximas" apaga desse vencimento em diante; "Todas" apaga o lote pendente.
  const [deleteBatch, setDeleteBatch] = useState<{ id: string; anchor: string; nextCount: number; allCount: number; totalLote: number; kind: "compra" | "crediario" } | null>(null);
  // Lixeira em linha de compra parcelada — Histórico ou Contas a pagar —
  // abre o MESMO diálogo de lote; transação avulsa segue na confirmação simples.
  const pedirExclusao = async (tx: any) => {
    const pid = tx.installmentPurchaseId;
    // Crediário: parcelas a receber são irmãs pelo pedido — mesmo lote da
    // parcelada de despesa (desta em diante / todas as pendentes).
    if (!pid && tx.type === "receita" && tx.orderId && tx.status === "pendente") {
      const due = tx.dueDate || tx.date;
      const irmas = allPendingTxs.filter((p: any) => p.type === "receita" && p.orderId === tx.orderId);
      if (irmas.length > 1) {
        setDeleteBatch({
          id: tx.orderId,
          anchor: due,
          nextCount: irmas.filter((p: any) => (p.dueDate || p.date) >= due).length,
          allCount: irmas.length,
          totalLote: irmas.length,
          kind: "crediario",
        });
        return;
      }
      setDeleteConfirmTxId(tx.id);
      return;
    }
    // Conta PENDENTE de despesa recorrente: escolher entre só a conta ou levar
    // a recorrente junto (com as pendentes dela). Recorrente já apagada (órfã)
    // cai na confirmação simples.
    if (!pid && tx.recurringExpenseId && tx.status === "pendente") {
      try {
        const rules: any[] = await customFetch("/api/financial/recurring");
        const r = rules.find((x: any) => x.id === tx.recurringExpenseId);
        if (r) {
          setDeleteRec({ txId: tx.id, ruleId: r.id, anchor: tx.dueDate || tx.date });
          return;
        }
      } catch { /* segue pra confirmação simples */ }
    }
    // Linha paga (parcela ou entrada) é história — o lote só mexe em pendentes;
    // apagar uma paga é decisão linha a linha, na confirmação simples.
    if (!pid || tx.status !== "pendente") {
      setDeleteConfirmTxId(tx.id);
      return;
    }
    const due = tx.dueDate || tx.date;
    const irmas = allPendingTxs.filter((p: any) => p.installmentPurchaseId === pid);
    // Lote inteiro = pendentes + pagas + entrada (o resumo diz o que existe).
    let extras = 0;
    try {
      const r: any = await customFetch(`/api/financial/installments/${pid}`);
      extras = (r?.paidParcels || 0) + (r?.downPayment >= 0.01 ? 1 : 0);
    } catch {}
    setDeleteBatch({
      id: pid,
      anchor: due,
      nextCount: irmas.filter((p: any) => (p.dueDate || p.date) >= due).length,
      allCount: irmas.length,
      totalLote: irmas.length + extras,
      kind: "compra",
    });
  };
  const [deleteRec, setDeleteRec] = useState<{ txId: string; ruleId: string; anchor: string } | null>(null);
  // Série infinita não tem "todas": os alcances são temporais — só esta, ou
  // desta em diante (encerra a repetição; pendente de mês ANTERIOR fica).
  const handleDeleteRec = async (scope: "conta" | "next") => {
    if (!deleteRec) return;
    try {
      if (scope === "conta") {
        deleteTxMutation.mutate({ id: deleteRec.txId });
      } else {
        await customFetch(`/api/financial/recurring/${deleteRec.ruleId}?scope=pending&from=${encodeURIComponent(deleteRec.anchor)}`, { method: "DELETE" });
        queryClient.invalidateQueries({ queryKey: getListTransactionsQueryKey() });
        queryClient.invalidateQueries({ queryKey: getGetFinancialSummaryQueryKey() });
        queryClient.invalidateQueries({ queryKey: ["financial-recurring"] });
        toast({ title: "Despesa recorrente encerrada" });
      }
    } catch {
      toast({ title: errorTitle("excluir"), variant: "destructive" });
    } finally {
      setDeleteRec(null);
    }
  };
  const runDeleteBatch = async (scope: "next" | "all" | "full") => {
    if (!deleteBatch) return;
    const qs = scope === "next" ? `?from=${encodeURIComponent(deleteBatch.anchor)}` : scope === "full" ? "?scope=all" : "";
    const base = deleteBatch.kind === "crediario" ? "credit-installments" : "installments";
    try {
      const r: any = await customFetch(`/api/financial/${base}/${deleteBatch.id}${qs}`, { method: "DELETE" });
      queryClient.invalidateQueries({ queryKey: getListTransactionsQueryKey() });
      queryClient.invalidateQueries({ queryKey: getGetFinancialSummaryQueryKey() });
      toast({ title: `${r?.deleted ?? 0} parcela(s) excluída(s)` });
    } catch {
      toast({ title: errorTitle("excluir as parcelas"), variant: "destructive" });
    } finally {
      setDeleteBatch(null);
    }
  };

  const [editingPurchase, setEditingPurchase] = useState<any | null>(null);
  const openEdit = async (tx: Transaction) => {
    // Linha de compra parcelada abre a COMPRA inteira (relato: lápis na
    // entrada mostrava só a entrada). Com parcela já paga, a edição em lote
    // reescreveria histórico — cai na edição da linha, avisando.
    const pid = (tx as any).installmentPurchaseId;
    if (pid) {
      try {
        const p: any = await customFetch(`/api/financial/installments/${pid}`);
        setEditingPurchase(p);
        return;
      } catch { /* segue pra edição da linha */ }
    }
    // Conta PENDENTE de recorrente: editar a conta do mês era armadilha — a
    // reconciliação desfazia. Escolha: só esta (desvincula) ou a recorrente.
    if ((tx as any).recurringExpenseId && (tx as any).status === "pendente") {
      try {
        const rules: any[] = await customFetch("/api/financial/recurring");
        const r = rules.find((x: any) => x.id === (tx as any).recurringExpenseId);
        if (r) {
          setEditChoice({ tx, rule: r });
          return;
        }
      } catch { /* órfã: edição simples */ }
    }
    setEditingTx(tx);
    setModalOpen(true);
  };
  const [editChoice, setEditChoice] = useState<{ tx: any; rule: any } | null>(null);
  const [detachTx, setDetachTx] = useState(false);
  const [editingRule, setEditingRule] = useState<any | null>(null);

  const closeModal = () => {
    setModalOpen(false);
    setEditingTx(null);
  };

  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: getGetFinancialSummaryQueryKey() });
    queryClient.invalidateQueries({ queryKey: getListTransactionsQueryKey() });
  }, [queryClient]);

  return (
    <div className="space-y-6 pb-20 md:pb-0">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Financeiro</h1>
          <p className="text-muted-foreground mt-1">Controle de caixa, receitas e despesas.</p>
        </div>
      </div>

      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <Card className="p-4 border-l-2 border-l-green-500 border-b-2 border-b-green-500">
            <p className="text-xs font-medium text-muted-foreground mb-1">Receitas ({range.label})</p>
            <h3 className="text-lg font-bold text-green-600 dark:text-green-400">
              {loadingSummary ? "..." : formatCurrency(summary?.monthRevenue || 0)}
            </h3>
          </Card>
          <Card className="p-4 border-l-2 border-l-red-500 border-b-2 border-b-red-500">
            <p className="text-xs font-medium text-muted-foreground mb-1">Despesas ({range.label})</p>
            <h3 className="text-lg font-bold text-red-600 dark:text-red-400">
              {loadingSummary ? "..." : formatCurrency(summary?.monthExpenses || 0)}
            </h3>
          </Card>
        </div>
        <Card className={`p-4 border-l-2 border-b-2 ${(summary?.balance || 0) >= 0 ? 'border-l-green-500 border-b-green-500' : 'border-l-red-500 border-b-red-500'}`}>
          <p className="text-xs font-medium text-muted-foreground mb-1">Saldo ({range.label})</p>
          <h3 className={`text-lg font-bold ${(summary?.balance || 0) >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
            {loadingSummary ? "..." : formatCurrency(summary?.balance || 0)}
          </h3>
        </Card>
        {/* Margem: só faz sentido com custo cadastrado nos produtos. Quando a
            loja lança as compras de material como despesa (costExpenseEnabled
            false), este é o ÚNICO lugar onde o custo aparece. */}
        {((summary as any)?.productCost || 0) > 0 && (
          <Card className="p-4">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-xs font-medium text-muted-foreground">Custo dos produtos vendidos</p>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  {(summary as any)?.costExpenseEnabled === false
                    ? "Só cálculo — não entra no saldo acima."
                    : "Já incluído nas despesas acima."}
                </p>
              </div>
              <span className="shrink-0 font-bold text-sm text-red-600 dark:text-red-400">
                {formatCurrency((summary as any)?.productCost || 0)}
              </span>
            </div>
            <div className="flex items-center justify-between gap-3 mt-2 pt-2 border-t border-border/50">
              <p className="text-xs font-medium text-muted-foreground">Margem (receitas − custo)</p>
              <span className="shrink-0 font-bold text-sm text-green-600 dark:text-green-400">
                {formatCurrency((summary?.monthRevenue || 0) - ((summary as any)?.productCost || 0))}
              </span>
            </div>
          </Card>
        )}
      </div>

      {/* Lado a lado, como Filtros+Pedido na página de Pedidos. Adicionar é um
          menu: transação avulsa ou despesa recorrente. */}
      <div className="flex gap-2">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button className="flex-1 shadow-md shadow-primary/20" data-testid="finance-add">
              <Plus className="w-4 h-4 mr-2" />
              Adicionar
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            <DropdownMenuItem onClick={() => { setEditingTx(null); setModalOpen(true); }} data-testid="finance-add-tx">
              <ArrowUpRight className="w-4 h-4 mr-2" /> Receita ou despesa
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setCreatingRecurring(true)} data-testid="finance-add-recurring">
              <Repeat className="w-4 h-4 mr-2" /> Despesa recorrente
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setCreatingInstallments(true)} data-testid="finance-add-installments">
              <Layers className="w-4 h-4 mr-2" /> Compra parcelada
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <Button variant="outline" onClick={handleExport} disabled={exporting} className="flex-1" data-testid="finance-export">
          {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
          Exportar Excel
        </Button>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <PeriodFilter state={periodState} onChange={setPeriodState} testId="finance-period" />
      </div>

      {((summary as any)?.overdueCount > 0 || (summary as any)?.dueTodayCount > 0) && (
        <div className="flex items-center gap-2 text-xs bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 text-red-800 dark:text-red-300 rounded-lg px-3 py-2">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          <span>
            {(summary as any)?.overdueCount > 0 && <>{(summary as any).overdueCount} conta{(summary as any).overdueCount > 1 ? "s" : ""} <strong>vencida{(summary as any).overdueCount > 1 ? "s" : ""}</strong>. </>}
            {(summary as any)?.dueTodayCount > 0 && <>{(summary as any).dueTodayCount} vence{(summary as any).dueTodayCount > 1 ? "m" : ""} <strong>hoje</strong>.</>}
          </span>
        </div>
      )}

      <div className="flex gap-2 overflow-x-auto pb-1 hide-scrollbar">
        {([
          { id: "historico", label: "Extrato" },
          { id: "contas", label: `Contas a pagar${pendingTxs.length > 0 ? ` (${pendingTxs.length})` : ""}` },
          { id: "receber", label: `A receber${receivableTxs.length > 0 ? ` (${receivableTxs.length})` : ""}` },
          { id: "recorrentes", label: "Despesas recorrentes" },
        ] as const).map((s) => (
          <button
            key={s.id}
            onClick={() => setSection(s.id)}
            className={`px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-colors ${section === s.id ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:bg-muted/70"}`}
            data-testid={`finance-section-${s.id}`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {section === "contas" && (pendingTxs.length === 0 ? (
        <Card className="p-8 text-center text-sm text-muted-foreground">
          Nenhuma conta a pagar. 🎉 Despesas pendentes (avulsas ou recorrentes) aparecem aqui até o pagamento ser registrado.
        </Card>
      ) : (
        <Card className="overflow-hidden border-amber-300 dark:border-amber-800">
          <div className="p-4 border-b border-border/50 bg-amber-50/60 dark:bg-amber-950/20">
            <h3 className="font-bold text-lg">Contas a pagar</h3>
            <p className="text-xs text-muted-foreground">Só entram no caixa quando o pagamento for registrado.</p>
          </div>
          <div className="divide-y divide-border/50">
            {pendingTxs.map((tx: any) => {
              const due = tx.dueDate || tx.date;
              const overdue = due < todayIso;
              const isToday = due === todayIso;
              return (
                <div key={tx.id} className="flex items-center gap-3 px-4 py-3">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{tx.description || "Despesa"}</p>
                    <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                      <span className="text-xs text-muted-foreground">Vence {due.split("-").reverse().join("/")}</span>
                      {overdue && <span className="text-[10px] font-bold uppercase bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300 px-1.5 py-0.5 rounded">Vencida</span>}
                      {isToday && <span className="text-[10px] font-bold uppercase bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 px-1.5 py-0.5 rounded">Vence hoje</span>}
                      {tx.recurringExpenseId && <Repeat className="w-3 h-3 text-muted-foreground" />}
                      {tx.installmentPurchaseId && <Layers className="w-3 h-3 text-muted-foreground" />}
                    </div>
                    <p className="md:hidden mt-0.5 font-bold text-sm text-red-600 dark:text-red-400">-{formatCurrency(tx.amount)}</p>
                  </div>
                  <span className="hidden md:block shrink-0 font-bold text-sm text-red-600 dark:text-red-400">-{formatCurrency(tx.amount)}</span>
                  <Button size="sm" variant="outline" className="shrink-0" onClick={() => setPayingTx(tx)} data-testid={`finance-pay-${tx.id}`}>
                    <CheckCircle2 className="w-4 h-4 mr-1" /> Pagar
                  </Button>
                  {/* <md: 3 botões esmagavam a descrição — Editar/Excluir viram ⋮, como no Extrato. */}
                  <div className="hidden md:flex items-center gap-1 shrink-0">
                    <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => openEdit(tx as any)} aria-label={`Editar ${tx.description || "conta"}`}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button size="icon" variant="outline" className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10" aria-label={`Excluir ${tx.description || "conta"}`}
                      onClick={() => pedirExclusao(tx)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button size="icon" variant="ghost" className="md:hidden h-11 w-11 shrink-0" aria-label={`Ações da conta ${tx.description || ""}`.trim()}>
                        <MoreVertical className="w-5 h-5" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => openEdit(tx as any)}>
                        <Pencil className="w-4 h-4 mr-2" /> Editar
                      </DropdownMenuItem>
                      <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={() => pedirExclusao(tx)}>
                        <Trash2 className="w-4 h-4 mr-2" /> Excluir
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              );
            })}
          </div>
        </Card>
      ))}

      {section === "receber" && (receivableTxs.length === 0 ? (
        <Card className="p-8 text-center text-sm text-muted-foreground">
          Nada a receber. Vendas no crediário geram as parcelas aqui, com lembrete de vencimento, até o recebimento ser registrado.
        </Card>
      ) : (
        <Card className="overflow-hidden border-green-300 dark:border-green-800">
          <div className="p-4 border-b border-border/50 bg-green-50/60 dark:bg-green-950/20">
            <h3 className="font-bold text-lg">A receber</h3>
            <p className="text-xs text-muted-foreground">Parcelas de crediário e outras receitas pendentes. Só entram no saldo quando o recebimento for registrado.</p>
          </div>
          <div className="divide-y divide-border/50">
            {receivableTxs.map((tx: any) => {
              const due = tx.dueDate || tx.date;
              const overdue = due < todayIso;
              const isToday = due === todayIso;
              return (
                <div key={tx.id} className="flex items-center gap-3 px-4 py-3">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{tx.description || "Receita"}</p>
                    <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                      <span className="text-xs text-muted-foreground">Vence {due.split("-").reverse().join("/")}</span>
                      {overdue && <span className="text-[10px] font-bold uppercase bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300 px-1.5 py-0.5 rounded">Vencida</span>}
                      {isToday && <span className="text-[10px] font-bold uppercase bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 px-1.5 py-0.5 rounded">Vence hoje</span>}
                      {tx.paymentMethod && <span className="text-[10px] font-medium bg-muted text-muted-foreground px-1.5 py-0.5 rounded">{tx.paymentMethod}</span>}
                    </div>
                    <p className="md:hidden mt-0.5 font-bold text-sm text-green-600 dark:text-green-400">+{formatCurrency(tx.amount)}</p>
                  </div>
                  <span className="hidden md:block shrink-0 font-bold text-sm text-green-600 dark:text-green-400">+{formatCurrency(tx.amount)}</span>
                  <Button size="sm" variant="outline" className="shrink-0" onClick={() => setPayingTx(tx)} data-testid={`finance-receive-${tx.id}`}>
                    <CheckCircle2 className="w-4 h-4 mr-1" /> Receber
                  </Button>
                  <div className="hidden md:flex items-center gap-1 shrink-0">
                    <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => openEdit(tx as any)} aria-label={`Editar ${tx.description || "conta"}`}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button size="icon" variant="outline" className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10" aria-label={`Excluir ${tx.description || "conta"}`}
                      onClick={() => pedirExclusao(tx)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button size="icon" variant="ghost" className="md:hidden h-11 w-11 shrink-0" aria-label={`Ações da conta ${tx.description || ""}`.trim()}>
                        <MoreVertical className="w-5 h-5" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => openEdit(tx as any)}>
                        <Pencil className="w-4 h-4 mr-2" /> Editar
                      </DropdownMenuItem>
                      <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={() => pedirExclusao(tx)}>
                        <Trash2 className="w-4 h-4 mr-2" /> Excluir
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              );
            })}
          </div>
        </Card>
      ))}

      {section === "recorrentes" && <RecurringExpensesCard />}

      {section === "historico" && (
      <Card className="overflow-hidden">
        <div className="p-4 border-b border-border/50 bg-muted/10">
          <h3 className="font-bold text-lg">Extrato</h3>
        </div>

        {loadingTx ? (
          <div className="p-8 flex justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
          </div>
        ) : !txData?.transactions || txData.transactions.length === 0 ? (
          <div className="p-12 text-center text-muted-foreground">
            Nenhuma transação registrada.
          </div>
        ) : (
          <div className="divide-y divide-border/50">
            {txData.transactions.map(tx => (
              <div key={tx.id} className="flex items-center gap-3 px-4 py-3 hover:bg-muted/10 transition-colors">
                <div className={`shrink-0 w-9 h-9 rounded-full flex items-center justify-center ${tx.type === 'receita' ? 'bg-green-100 dark:bg-green-900/30 text-green-600' : 'bg-red-100 dark:bg-red-900/30 text-red-600'}`}>
                  {tx.type === 'receita' ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">
                    {tx.description || (tx.orderId ? "Pedido" : "Sem descrição")}
                  </p>
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-0.5">
                    <span className="text-xs text-muted-foreground">
                      {format(parseISO((tx as any).status === "pendente" ? ((tx as any).dueDate || tx.date) : tx.date), "dd/MM/yyyy", { locale: ptBR })}
                    </span>
                    <span className="inline-flex px-1.5 py-0.5 bg-muted rounded text-[10px] text-muted-foreground">
                      {tx.category?.name || "Geral"}
                    </span>
                    {(tx as any).paymentMethod && (
                      <span className="inline-flex px-1.5 py-0.5 bg-muted rounded text-[10px] text-muted-foreground">
                        {(tx as any).paymentMethod}
                      </span>
                    )}
                    {(tx as any).invoice?.itens?.length > 0 && <EtiquetaNota nota={(tx as any).invoice} descricao={tx.description || "Compra"} />}
                    {(tx as any).status === "pendente" && (
                      <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-bold uppercase ${((tx as any).dueDate || tx.date) < todayIso ? "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300" : "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300"}`}>
                        {((tx as any).dueDate || tx.date) < todayIso ? "Vencida" : "Pendente"}
                      </span>
                    )}
                  </div>
                </div>
                <span className={`shrink-0 text-right font-bold text-sm whitespace-nowrap ${tx.type === 'receita' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                  {tx.type === 'receita' ? '+' : '-'}{formatCurrency(tx.amount)}
                </span>
                {/* Em >=md: dois botões ícone inline (Editar/Excluir). Em <md
                    a row já tem 5 elementos (ícone+descrição+data+badge+valor),
                    inflar com 2 botões de 32px estoura - vira ⋮ menu. */}
                <div className="hidden md:flex items-center gap-1 shrink-0">
                  <Button
                    type="button"
                    size="icon"
                    variant="outline"
                    className="h-8 w-8 text-primary hover:text-primary hover:bg-primary/10"
                    onClick={() => openEdit(tx)}
                    aria-label={`Editar ${tx.description || "transação"}`}
                  >
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button
                    type="button"
                    size="icon"
                    variant="outline"
                    className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
                    onClick={() => pedirExclusao(tx)}
                    disabled={deleteTxMutation.isPending}
                    aria-label={`Excluir ${tx.description || "transação"}`}
                  >
                    {deleteTxMutation.isPending && deleteTxMutation.variables?.id === tx.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4" />
                    )}
                  </Button>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      className="md:hidden h-11 w-11 shrink-0"
                      aria-label={`Ações de ${tx.description || "transação"}`}
                    >
                      <MoreVertical className="w-5 h-5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => openEdit(tx)}>
                      <Pencil className="w-4 h-4 mr-2" /> Editar
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => pedirExclusao(tx)}
                      className="text-destructive focus:text-destructive"
                    >
                      <Trash2 className="w-4 h-4 mr-2" /> Excluir
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            ))}
          </div>
        )}
      </Card>
      )}

      <TransactionModal isOpen={isModalOpen} onClose={closeModal} editingTx={editingTx} detachRecurring={detachTx} />

      {editingPurchase && (
        <InstallmentsModal
          purchase={editingPurchase}
          onClose={() => setEditingPurchase(null)}
          onSaved={() => {
            setEditingPurchase(null);
            queryClient.invalidateQueries({ queryKey: getListTransactionsQueryKey() });
            queryClient.invalidateQueries({ queryKey: getGetFinancialSummaryQueryKey() });
          }}
        />
      )}
      {creatingInstallments && (
        <InstallmentsModal
          onClose={() => setCreatingInstallments(false)}
          onSaved={() => {
            setCreatingInstallments(false);
            queryClient.invalidateQueries({ queryKey: getListTransactionsQueryKey() });
            queryClient.invalidateQueries({ queryKey: getGetFinancialSummaryQueryKey() });
          }}
        />
      )}
      {creatingRecurring && (
        <RecurringModal
          rule={null}
          onClose={() => setCreatingRecurring(false)}
          onSaved={() => {
            setCreatingRecurring(false);
            queryClient.invalidateQueries({ queryKey: ["financial-recurring"] });
            queryClient.invalidateQueries({ queryKey: getListTransactionsQueryKey() });
            queryClient.invalidateQueries({ queryKey: getGetFinancialSummaryQueryKey() });
          }}
        />
      )}

      {payingTx && <PayDialog tx={payingTx} onClose={() => setPayingTx(null)} />}

      <AlertDialog open={!!editChoice} onOpenChange={(o) => !o && setEditChoice(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Editar o quê?</AlertDialogTitle>
          </AlertDialogHeader>
          <div className="flex flex-col gap-2 py-1">
            <Button variant="outline" className="justify-start" onClick={() => {
              const tx = editChoice!.tx;
              setEditChoice(null);
              setDetachTx(true);
              setEditingTx(tx);
              setModalOpen(true);
            }}>
              Só esta conta <span className="ml-1 text-muted-foreground">— deixa de repetir</span>
            </Button>
            <Button variant="outline" className="justify-start" onClick={() => {
              const rule = editChoice!.rule;
              setEditChoice(null);
              setEditingRule(rule);
            }}>
              A despesa recorrente <span className="ml-1 text-muted-foreground">— todo mês</span>
            </Button>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {editingRule && (
        <RecurringModal
          rule={editingRule}
          onClose={() => setEditingRule(null)}
          onSaved={() => {
            setEditingRule(null);
            queryClient.invalidateQueries({ queryKey: getListTransactionsQueryKey() });
            queryClient.invalidateQueries({ queryKey: getGetFinancialSummaryQueryKey() });
            queryClient.invalidateQueries({ queryKey: ["financial-recurring"] });
          }}
        />
      )}

      {/* AlertDialog único pra confirmação de exclusão. Substitui confirm()
          nativo (anti-padrão do PR 2). Microcopy segue lib/messages.ts:
          título causa-explícita, descrição ação-explícita, botão destrutivo
          com verbo claro. */}
      <AlertDialog open={!!deleteConfirmTxId} onOpenChange={(open) => !open && setDeleteConfirmTxId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir esta transação?</AlertDialogTitle>
            <AlertDialogDescription>
              A transação some do extrato e não dá pra desfazer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (deleteConfirmTxId) deleteTxMutation.mutate({ id: deleteConfirmTxId });
                setDeleteConfirmTxId(null);
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!deleteRec} onOpenChange={(open) => !open && setDeleteRec(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir esta conta?</AlertDialogTitle>
            <AlertDialogDescription>Não dá pra desfazer.</AlertDialogDescription>
          </AlertDialogHeader>
          <div className="flex flex-col gap-2 py-1">
            <Button variant="outline" className="justify-start text-destructive hover:text-destructive hover:bg-destructive/10" onClick={() => handleDeleteRec("conta")}>
              Só esta
            </Button>
            <Button variant="outline" className="justify-start text-destructive hover:text-destructive hover:bg-destructive/10" onClick={() => handleDeleteRec("next")}>
              Desta em diante <span className="ml-1 text-muted-foreground">— para de repetir</span>
            </Button>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!deleteBatch} onOpenChange={(open) => !open && setDeleteBatch(null)}>
        <AlertDialogContent>
          {/* Alcances que coincidem não viram botão repetido; com pagas/entrada
              a decisão de incluí-las é da LOJISTA — "A compra inteira". */}
          {deleteBatch && deleteBatch.nextCount === deleteBatch.allCount && deleteBatch.totalLote === deleteBatch.allCount ? (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle>{deleteBatch.allCount === 1 ? "Excluir a parcela pendente?" : `Excluir as ${deleteBatch.allCount} parcelas pendentes?`}</AlertDialogTitle>
                <AlertDialogDescription>Não dá pra desfazer.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                <AlertDialogAction onClick={() => runDeleteBatch("all")} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                  Excluir {deleteBatch.allCount === 1 ? "a parcela" : `${deleteBatch.allCount} parcelas`}
                </AlertDialogAction>
              </AlertDialogFooter>
            </>
          ) : (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle>Excluir quais parcelas?</AlertDialogTitle>
                <AlertDialogDescription>Não dá pra desfazer.</AlertDialogDescription>
              </AlertDialogHeader>
              <div className="flex flex-col gap-2 py-1">
                {deleteBatch && deleteBatch.nextCount !== deleteBatch.allCount && (
                  <Button variant="outline" className="justify-start text-destructive hover:text-destructive hover:bg-destructive/10" onClick={() => runDeleteBatch("next")}>
                    Desta em diante <span className="ml-1 text-muted-foreground">({deleteBatch.nextCount})</span>
                  </Button>
                )}
                <Button variant="outline" className="justify-start text-destructive hover:text-destructive hover:bg-destructive/10" onClick={() => runDeleteBatch("all")}>
                  Todas as pendentes <span className="ml-1 text-muted-foreground">({deleteBatch?.allCount ?? 0})</span>
                </Button>
                {deleteBatch && deleteBatch.totalLote > deleteBatch.allCount && (
                  <Button variant="outline" className="justify-start text-destructive hover:text-destructive hover:bg-destructive/10" onClick={() => runDeleteBatch("full")}>
                    A compra inteira <span className="ml-1 text-muted-foreground">({deleteBatch.totalLote})</span>
                  </Button>
                )}
              </div>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancelar</AlertDialogCancel>
              </AlertDialogFooter>
            </>
          )}
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

interface FinancialCategory {
  id: string;
  name: string;
  type: string;
}

// Registrar pagamento de conta pendente: forma + data (default hoje).
function PayDialog({ tx, onClose }: { tx: any; onClose: () => void }) {
  const [paymentMethod, setPaymentMethod] = useState<string>(tx.paymentMethod || "PIX");
  const [date, setDate] = useState(localIsoDate());
  const [saving, setSaving] = useState(false);
  // Dinheiro costuma sair da gaveta do caixa — espelhar como Retirada evita
  // falta no fechamento. Ligado por padrão; quem paga do bolso desliga.
  const [cashOut, setCashOut] = useState(true);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: cashData } = useQuery<any>({
    queryKey: ["cash-current"],
    queryFn: () => customFetch("/api/cash/current"),
    staleTime: 15000,
  });
  const openCashSession = cashData?.session ?? null;
  const isReceita = tx.type === "receita";
  const showCashOut = paymentMethod === "Dinheiro" && !!openCashSession;

  const handlePay = async () => {
    setSaving(true);
    try {
      await customFetch(`/api/financial/transactions/${tx.id}/pay`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ paymentMethod, date, cashOut: showCashOut && cashOut }),
      });
      queryClient.invalidateQueries({ queryKey: getListTransactionsQueryKey() });
      queryClient.invalidateQueries({ queryKey: getGetFinancialSummaryQueryKey() });
      queryClient.invalidateQueries({ queryKey: ["cash-current"] });
      toast({ title: isReceita ? "Recebimento registrado!" : "Pagamento registrado!" });
      onClose();
    } catch (err: any) {
      toast({ title: errorTitle("registrar o pagamento"), description: TRY_AGAIN_DESCRIPTION, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-sm max-h-[90dvh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isReceita ? "Registrar recebimento" : "Registrar pagamento"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-sm">
            <span className="font-semibold">{tx.description || (isReceita ? "Receita" : "Despesa")}</span> — {formatCurrency(tx.amount)}
          </p>
          <div className="space-y-1.5">
            <Label>{isReceita ? "Como recebeu" : "Forma de pagamento"}</Label>
            <select className="w-full border rounded-md p-2 text-sm bg-background h-10" value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)}>
              {EXPENSE_PAYMENT_METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <div className="space-y-1.5">
            <Label>Data do pagamento</Label>
            <DataBr value={date} onChange={setDate} />
          </div>
          {showCashOut && (
            <label className="flex items-start gap-2.5 rounded-lg border p-3 cursor-pointer" data-testid="finance-pay-cashout">
              <input type="checkbox" checked={cashOut} onChange={(e) => setCashOut(e.target.checked)} className="mt-0.5 h-4 w-4 accent-primary" />
              <span className="text-sm">
                <span className="font-medium">{isReceita ? "Guardar na gaveta do caixa" : "Retirar da gaveta do caixa"}</span>
                <span className="block text-xs text-muted-foreground mt-0.5">
                  {isReceita
                    ? `Registra uma entrada de ${formatCurrency(tx.amount)} no caixa aberto, pra gaveta bater no fechamento. Desmarque se o dinheiro não foi pra gaveta.`
                    : `Registra uma retirada de ${formatCurrency(tx.amount)} no caixa aberto, pra gaveta bater no fechamento. Desmarque se o dinheiro não saiu da gaveta.`}
                </span>
              </span>
            </label>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancelar</Button>
          <Button onClick={handlePay} disabled={saving} data-testid="finance-pay-confirm">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
            {isReceita ? "Confirmar recebimento" : "Confirmar pagamento"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Despesas recorrentes: geram uma conta pendente por mês no dia do vencimento.
function RecurringExpensesCard() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { data: rules = [], refetch } = useQueryRecurring();
  const [editing, setEditing] = useState<any | null>(null);
  const [deleteRule, setDeleteRule] = useState<any | null>(null);

  const invalidateAll = () => {
    refetch();
    queryClient.invalidateQueries({ queryKey: getListTransactionsQueryKey() });
    queryClient.invalidateQueries({ queryKey: getGetFinancialSummaryQueryKey() });
  };

  const toggleActive = async (rule: any, next: boolean) => {
    try {
      await customFetch(`/api/financial/recurring/${rule.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ isActive: next }),
      });
      invalidateAll();
    } catch {
      toast({ title: errorTitle("atualizar a despesa recorrente"), variant: "destructive" });
    }
  };

  // Alcance é escolha da lojista, como na parcelada: só a regra, regra +
  // pendentes, ou regra + todas as contas geradas (pagas incluídas).
  const handleDelete = async (scope: "rule" | "pending" | "all") => {
    if (!deleteRule) return;
    const qs = scope === "rule" ? "" : `?scope=${scope}`;
    try {
      await customFetch(`/api/financial/recurring/${deleteRule.id}${qs}`, { method: "DELETE" });
      invalidateAll();
      toast({ title: "Despesa recorrente excluída" });
    } catch {
      toast({ title: errorTitle("excluir a despesa recorrente"), variant: "destructive" });
    } finally {
      setDeleteRule(null);
    }
  };

  return (
    <Card className="overflow-hidden">
      <div className="p-4 border-b border-border/50 bg-muted/10">
        <h3 className="font-bold text-lg flex items-center gap-2"><Repeat className="w-4 h-4 text-primary" /> Despesas recorrentes</h3>
        <p className="text-xs text-muted-foreground">Todo mês entra como conta a pagar no dia do vencimento.</p>
      </div>
      {rules.length === 0 ? (
        <div className="p-6 text-center text-sm text-muted-foreground">
          Nenhuma despesa recorrente. Use <strong className="text-foreground">Adicionar → Despesa recorrente</strong> pra cadastrar aluguel, energia, internet… e receber o lembrete no vencimento.
        </div>
      ) : (
        <div className="divide-y divide-border/50">
          {rules.map((rule: any) => (
            <div key={rule.id} className={`flex items-center gap-3 px-4 py-3 ${rule.isActive ? "" : "opacity-60"}`}>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm truncate">{rule.name}</p>
                <p className="text-xs text-muted-foreground">
                  Todo dia {rule.dueDay}{rule.startDate && rule.startDate > brDayKey(new Date()) ? ` · a partir de ${rule.startDate.substring(8, 10)}/${rule.startDate.substring(5, 7)}` : ""}{rule.paymentMethod ? ` · ${rule.paymentMethod}` : ""}
                </p>
                <p className="md:hidden mt-0.5 font-bold text-sm">{formatCurrency(rule.amount)}</p>
              </div>
              <span className="hidden md:block shrink-0 font-bold text-sm">{formatCurrency(rule.amount)}</span>
              <Switch checked={rule.isActive} onCheckedChange={(v) => toggleActive(rule, v)} aria-label="Ativa" />
              <div className="hidden md:flex items-center gap-1 shrink-0">
                <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => setEditing(rule)} aria-label={`Editar a recorrente ${rule.name}`}>
                  <Pencil className="w-4 h-4" />
                </Button>
                <Button size="icon" variant="outline" className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10" onClick={() => setDeleteRule(rule)} aria-label={`Excluir a recorrente ${rule.name}`}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button size="icon" variant="ghost" className="md:hidden h-11 w-11 shrink-0" aria-label={`Ações da recorrente ${rule.name}`}>
                    <MoreVertical className="w-5 h-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => setEditing(rule)}>
                    <Pencil className="w-4 h-4 mr-2" /> Editar
                  </DropdownMenuItem>
                  <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={() => setDeleteRule(rule)}>
                    <Trash2 className="w-4 h-4 mr-2" /> Excluir
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <RecurringModal
          rule={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); invalidateAll(); }}
        />
      )}

      <AlertDialog open={!!deleteRule} onOpenChange={(o) => !o && setDeleteRule(null)}>
        <AlertDialogContent>
          {deleteRule && (deleteRule.txCount ?? 0) === 0 ? (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle>Excluir esta despesa recorrente?</AlertDialogTitle>
                <AlertDialogDescription>Não dá pra desfazer.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                <AlertDialogAction onClick={() => handleDelete("rule")} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                  Excluir
                </AlertDialogAction>
              </AlertDialogFooter>
            </>
          ) : (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle>Excluir esta despesa recorrente?</AlertDialogTitle>
                <AlertDialogDescription>Não dá pra desfazer.</AlertDialogDescription>
              </AlertDialogHeader>
              <div className="flex flex-col gap-2 py-1">
                <Button variant="outline" className="justify-start text-destructive hover:text-destructive hover:bg-destructive/10" onClick={() => handleDelete("rule")}>
                  Só ela <span className="ml-1 text-muted-foreground">— as contas ficam</span>
                </Button>
                {(deleteRule?.pendingCount ?? 0) > 0 && (
                  <Button variant="outline" className="justify-start text-destructive hover:text-destructive hover:bg-destructive/10" onClick={() => handleDelete("pending")}>
                    Com as contas pendentes <span className="ml-1 text-muted-foreground">({deleteRule.pendingCount})</span>
                  </Button>
                )}
                {(deleteRule?.txCount ?? 0) > (deleteRule?.pendingCount ?? 0) && (
                  <Button variant="outline" className="justify-start text-destructive hover:text-destructive hover:bg-destructive/10" onClick={() => handleDelete("all")}>
                    Com todas as contas <span className="ml-1 text-muted-foreground">({deleteRule.txCount})</span>
                  </Button>
                )}
              </div>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancelar</AlertDialogCancel>
              </AlertDialogFooter>
            </>
          )}
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}

function useQueryRecurring() {
  return useQuery<any[]>({
    queryKey: ["financial-recurring"],
    queryFn: () => customFetch("/api/financial/recurring"),
  });
}

function RecurringModal({ rule, onClose, onSaved }: { rule: any | null; onClose: () => void; onSaved: () => void }) {
  const [name, setName] = useState(rule?.name || "");
  const [amount, setAmount] = useState(rule ? String(rule.amount).replace(".", ",") : "");
  // Vencimento como DATA, não só dia do mês: o dia sozinho fazia a regra nascer
  // no mês corrente mesmo com o dia já vencido (relato Farliney, 29 de 102
  // contas geradas nasceram vencidas).
  const hoje = brDayKey(new Date());
  const [firstDue, setFirstDue] = useState<string>(
    rule ? (rule.startDate || `${hoje.substring(0, 7)}-${String(rule.dueDay).padStart(2, "0")}`) : hoje,
  );
  const [paymentMethod, setPaymentMethod] = useState<string>(rule?.paymentMethod || "");
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  const handleSave = async () => {
    const amountNum = parseFloat(amount.replace(",", "."));
    const day = parseInt(firstDue.substring(8, 10), 10);
    if (!name.trim() || !Number.isFinite(amountNum) || amountNum <= 0 || !/^\d{4}-\d{2}-\d{2}$/.test(firstDue) || !day) {
      toast({ title: "Preencha nome, valor e vencimento", variant: "destructive" });
      return;
    }
    setSaving(true);
    try {
      await customFetch(rule ? `/api/financial/recurring/${rule.id}` : "/api/financial/recurring", {
        method: rule ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: name.trim(), amount: amountNum, dueDay: day, startDate: firstDue, paymentMethod: paymentMethod || null }),
      });
      toast({ title: rule ? "Despesa recorrente atualizada!" : "Despesa recorrente criada!" });
      onSaved();
    } catch {
      toast({ title: errorTitle("salvar a despesa recorrente"), description: TRY_AGAIN_DESCRIPTION, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-sm max-h-[90dvh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{rule ? "Editar despesa recorrente" : "Nova despesa recorrente"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>Nome</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ex: Aluguel" autoFocus />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Valor</Label>
              <CurrencyInput value={amount} onChange={setAmount} />
            </div>
            <div className="space-y-1.5">
              <Label>{rule ? "Próximo vencimento" : "Primeiro vencimento"}</Label>
              <DataBr value={firstDue} onChange={setFirstDue} />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Forma de pagamento <span className="font-normal text-muted-foreground">(opcional)</span></Label>
            <select className="w-full border rounded-md p-2 text-sm bg-background h-10" value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)}>
              <option value="">Definir ao pagar</option>
              {EXPENSE_PAYMENT_METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancelar</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="finance-recurring-save">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Salvar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Compra parcelada: N contas a pagar nascem de uma vez, uma por mês, com o
// mesmo installment_purchase_id. A última parcela absorve o resíduo do
// arredondamento (R$ 1.000 em 3x: 333,33 + 333,33 + 333,34).
function InstallmentsModal({ onClose, onSaved, purchase }: { onClose: () => void; onSaved: () => void; purchase?: any }) {
  const [name, setName] = useState(purchase?.name || "");
  const [total, setTotal] = useState(purchase ? String(purchase.totalAmount).replace(".", ",") : "");
  const [entrada, setEntrada] = useState(purchase && purchase.downPayment >= 0.01 ? String(purchase.downPayment).replace(".", ",") : "");
  const [entradaData, setEntradaData] = useState<string>(purchase?.downPaymentDate || brDayKey(new Date()));
  const [count, setCount] = useState(purchase ? String(purchase.installments) : "2");
  const [firstDue, setFirstDue] = useState<string>(purchase?.firstDueDate || brDayKey(new Date()));
  const [paymentMethod, setPaymentMethod] = useState<string>(purchase?.paymentMethod || "");
  // Com parcelas pagas, a lojista decide o alcance da edição.
  const [escopo, setEscopo] = useState<"pending" | "all">("pending");
  const pagas: number = purchase?.paidParcels || 0;
  const somaPagas: number = purchase?.paidSum || 0;
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  const totalNum = parseFloat(total.replace(",", "."));
  const entradaNum = parseFloat(entrada.replace(",", ".")) || 0;
  const n = parseInt(count, 10);
  // Parcelas sobre o RESTANTE (total − entrada); floor na base espelha o
  // crediário e mantém a última ≥ as outras.
  const manterPagas = pagas > 0 && escopo === "pending";
  const financiado = Number.isFinite(totalNum) ? totalNum - entradaNum - (manterPagas ? somaPagas : 0) : 0;
  const slotsNovos = manterPagas ? n - pagas : n;
  const base = financiado > 0 && slotsNovos >= 1 ? Math.floor((financiado / slotsNovos) * 100) / 100 : 0;
  const last = base > 0 ? Math.round((financiado - base * (slotsNovos - 1)) * 100) / 100 : 0;

  const handleSave = async () => {
    if (!name.trim() || !(totalNum > 0) || !(n >= 2 && n <= 72) || !/^\d{4}-\d{2}-\d{2}$/.test(firstDue)) {
      toast({ title: "Preencha nome, valor, parcelas (2 a 72) e vencimento", variant: "destructive" });
      return;
    }
    if (pagas > 0 && n < pagas) {
      toast({ title: `Já tem ${pagas} parcela(s) paga(s) — parcelas não pode ser menor`, variant: "destructive" });
      return;
    }
    if (entradaNum < 0 || entradaNum >= totalNum) {
      toast({ title: "A entrada precisa ser menor que o valor total", variant: "destructive" });
      return;
    }
    if (entradaNum >= 0.01 && !/^\d{4}-\d{2}-\d{2}$/.test(entradaData)) {
      toast({ title: "Escolha a data da entrada", variant: "destructive" });
      return;
    }
    setSaving(true);
    try {
      await customFetch(purchase ? `/api/financial/installments/${purchase.purchaseId}` : "/api/financial/installments", {
        method: purchase ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: name.trim(), totalAmount: totalNum, installments: n, downPayment: entradaNum || null, downPaymentDate: entradaNum >= 0.01 ? entradaData : null, firstDueDate: firstDue, paymentMethod: paymentMethod || null, ...(purchase ? { scope: escopo } : {}) }),
      });
      toast({ title: purchase ? "Compra atualizada!" : `${n} parcelas criadas!` });
      onSaved();
    } catch {
      toast({ title: errorTitle("criar as parcelas"), description: TRY_AGAIN_DESCRIPTION, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-sm max-h-[90dvh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{purchase ? "Editar compra parcelada" : "Compra parcelada"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>Nome</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ex: Freezer" autoFocus />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Valor total</Label>
              <CurrencyInput value={total} onChange={setTotal} />
            </div>
            <div className="space-y-1.5">
              <Label>Entrada <span className="font-normal text-muted-foreground">(opcional)</span></Label>
              <CurrencyInput value={entrada} onChange={setEntrada} />
            </div>
          </div>
          {entradaNum >= 0.01 && (
            <div className="space-y-1.5">
              <Label>Data da entrada</Label>
              <DataBr value={entradaData} onChange={setEntradaData} />
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Parcelas</Label>
              <Input type="number" min={2} max={72} value={count} onChange={(e) => setCount(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>1º vencimento</Label>
              <DataBr value={firstDue} onChange={setFirstDue} />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Forma de pagamento <span className="font-normal text-muted-foreground">(opcional)</span></Label>
            <select className="w-full border rounded-md p-2 text-sm bg-background h-10" value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)}>
              <option value="">Definir ao pagar</option>
              {EXPENSE_PAYMENT_METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          {pagas > 0 && (
            <div className="space-y-1.5">
              <Label>{pagas} parcela(s) já paga(s)</Label>
              <div className="grid grid-cols-2 gap-2">
                <Button type="button" variant={escopo === "pending" ? "default" : "outline"} size="sm" onClick={() => setEscopo("pending")}>
                  Manter como estão
                </Button>
                <Button type="button" variant={escopo === "all" ? "default" : "outline"} size="sm" onClick={() => setEscopo("all")}>
                  Atualizar também
                </Button>
              </div>
            </div>
          )}
          {base > 0 && (
            <p className="text-xs text-muted-foreground">
              {entradaNum >= 0.01 ? `Entrada ${formatCurrency(entradaNum)} (paga em ${entradaData.substring(8, 10)}/${entradaData.substring(5, 7)}) + ` : ""}{manterPagas ? `${pagas} paga(s) (${formatCurrency(somaPagas)}) + ${slotsNovos}x` : `${n}x`} de {formatCurrency(base)}{last !== base ? ` · última ${formatCurrency(last)}` : ""}
            </p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancelar</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="finance-installments-save">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Salvar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TransactionModal({ isOpen, onClose, editingTx, detachRecurring }: { isOpen: boolean, onClose: () => void, editingTx?: Transaction | null, detachRecurring?: boolean }) {
  const isEditing = !!editingTx;
  // Data de HOJE no fuso da LOJA. Era toISOString() (UTC): depois das 21h em
  // Brasília o campo já vinha com o dia seguinte, o lançamento era salvo com a
  // data de amanhã e sumia do filtro "Hoje" — o lojista via "Transação salva!"
  // e o card em R$ 0,00 (relato Açaí ronconi; 572 lançamentos em 119 lojas
  // ficaram com data do dia seguinte desde abr/2026).
  const emptyForm = { type: "despesa", amount: "", date: brDayKey(new Date()), description: "", categoryId: "", paymentMethod: "", pending: false, dueDate: "" };
  const [formData, setFormData] = useState(emptyForm);

  useEffect(() => {
    if (!isOpen) return;
    if (editingTx) {
      setFormData({
        type: editingTx.type,
        amount: String(editingTx.amount).replace('.', ','),
        date: editingTx.date.slice(0, 10),
        description: editingTx.description ?? "",
        categoryId: editingTx.categoryId ?? "",
        paymentMethod: (editingTx as any).paymentMethod ?? "",
        pending: (editingTx as any).status === "pendente",
        dueDate: (editingTx as any).dueDate ? String((editingTx as any).dueDate).slice(0, 10) : "",
      });
    } else {
      setFormData(emptyForm);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, editingTx]);
  const [showNewCat, setShowNewCat] = useState(false);
  const [newCatName, setNewCatName] = useState("");
  const [editingCatId, setEditingCatId] = useState<string | null>(null);
  const [editCatName, setEditCatName] = useState("");
  const [deleteCatConfirmId, setDeleteCatConfirmId] = useState<string | null>(null);
  const { data: categories = [] } = useListFinancialCategories();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const filteredCategories = categories.filter(c => c.type === formData.type);

  const resetCatState = () => {
    setShowNewCat(false);
    setNewCatName("");
    setEditingCatId(null);
    setEditCatName("");
  };

  const createMutation = useCreateTransaction({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getListTransactionsQueryKey() });
        queryClient.invalidateQueries({ queryKey: getGetFinancialSummaryQueryKey() });
        toast({ title: "Transação salva!" });
        onClose();
        setFormData(emptyForm);
        resetCatState();
      },
      // Sem isto, falha no salvamento não dizia NADA: o modal ficava aberto e
      // o lojista concluía que tinha salvado. O de editar já avisava.
      onError: () => {
        toast({ title: errorTitle("salvar a transação"), description: TRY_AGAIN_DESCRIPTION, variant: "destructive" });
      },
    }
  });

  const updateMutation = useUpdateTransaction({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getListTransactionsQueryKey() });
        queryClient.invalidateQueries({ queryKey: getGetFinancialSummaryQueryKey() });
        toast({ title: "Transação atualizada!" });
        onClose();
        resetCatState();
      },
      onError: () => {
        toast({ title: errorTitle("atualizar a transação"), description: TRY_AGAIN_DESCRIPTION, variant: "destructive" });
      },
    },
  });

  const createCatMutation = useMutation({
    mutationFn: (name: string) => customFetch<FinancialCategory>("/api/financial/categories", {
      method: "POST",
      body: JSON.stringify({ name, type: formData.type }),
    }),
    onSuccess: (newCat) => {
      queryClient.invalidateQueries({ queryKey: getListFinancialCategoriesQueryKey() });
      setShowNewCat(false);
      setNewCatName("");
      if (newCat?.id) setFormData((f) => ({ ...f, categoryId: newCat.id }));
      toast({ title: "Categoria criada!" });
    },
  });

  const updateCatMutation = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      customFetch<FinancialCategory>(`/api/financial/categories/${id}`, { method: "PUT", body: JSON.stringify({ name }) }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getListFinancialCategoriesQueryKey() });
      setEditingCatId(null);
      setEditCatName("");
      toast({ title: "Categoria atualizada!" });
    },
  });

  const deleteCatMutation = useMutation({
    mutationFn: (id: string) => customFetch(`/api/financial/categories/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getListFinancialCategoriesQueryKey() });
      queryClient.invalidateQueries({ queryKey: getListTransactionsQueryKey() });
      setFormData((f) => ({ ...f, categoryId: "" }));
      toast({ title: "Categoria excluída!" });
    },
  });

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      {/* max-h + rolagem: o DialogContent cru não tem nenhum dos dois, então em
          tela pequena (ou com "Conta a pagar" ligado, que soma o campo de
          vencimento, ou com o teclado aberto) o rodapé com Cancelar/Salvar
          ficava FORA da tela e sem como rolar — "não tem a opção salvar"
          (maisq-salgados). Mesmo comportamento do ModalShell, que é o padrão. */}
      <DialogContent className="sm:max-w-[400px] max-h-[90dvh] overflow-y-auto">
        <DialogHeader><DialogTitle>{isEditing ? "Editar transação" : "Nova transação"}</DialogTitle></DialogHeader>
        <form onSubmit={(e) => {
          e.preventDefault();
          const { pending, dueDate, paymentMethod, ...rest } = formData;
          // Receita com data futura entra como "A receber" — quem decide é a
          // data, não um toggle (o servidor aplica a mesma regra). Ver o
          // aviso no campo Data.
          const receitaFutura = formData.type === "receita" && formData.date > localIsoDate();
          // ⚠️ Quem dá baixa em receita é o botão Receber, NUNCA o lápis.
          // Este form mandava "pago" + vencimento nulo em QUALQUER receita,
          // então abrir uma parcela de crediário que estava em "A receber" só
          // pra ajustar o texto já dava baixa nela (2 parcelas na base em
          // 18/09/2026). Editando algo que já é pendente, o pendente fica.
          const receitaPendente = formData.type === "receita" && isEditing && pending;
          const ficaPendente = (formData.type === "despesa" && pending) || receitaFutura || receitaPendente;
          // Receita não tem o campo Vencimento: a Data é o vencimento. Mandar o dueDate antigo do formulário deixava a
          // receita «A receber» com a Data nova e o vencimento velho (a lista mostra o vencimento; 01/11/2026, parcela
          // de crediário com data 09/11 e vencimento 09/10). Despesa segue com o campo Vencimento dela.
          const vencimento = formData.type === "receita" ? formData.date : (dueDate || formData.date);
          const data: any = {
            ...rest,
            amount: parseFloat(formData.amount.replace(',','.')),
            paymentMethod: paymentMethod || null,
            status: ficaPendente ? "pendente" : "pago",
            dueDate: ficaPendente ? vencimento : null,
          };
          if (isEditing && editingTx) {
            // "Só esta conta" de recorrente: desvincula — senão a
            // reconciliação desfaz a edição no próximo load.
            if (detachRecurring) data.detachRecurring = true;
            updateMutation.mutate({ id: editingTx.id, data });
          } else {
            createMutation.mutate({ data });
          }
        }}>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-2 p-1 bg-muted rounded-lg">
              <button type="button" onClick={() => { setFormData({...formData, type: 'despesa', categoryId: ''}); resetCatState(); }} className={`py-2 text-sm font-semibold rounded-md transition-all ${formData.type === 'despesa' ? 'bg-background shadow text-red-600' : 'text-muted-foreground hover:bg-muted-foreground/10'}`}>Despesa</button>
              <button type="button" onClick={() => { setFormData({...formData, type: 'receita', categoryId: ''}); resetCatState(); }} className={`py-2 text-sm font-semibold rounded-md transition-all ${formData.type === 'receita' ? 'bg-background shadow text-green-600' : 'text-muted-foreground hover:bg-muted-foreground/10'}`}>Receita</button>
            </div>
            
            <div className="space-y-2">
              <Label>Valor</Label>
              <CurrencyInput required value={formData.amount} onChange={(v) => setFormData({...formData, amount: v})} className="text-xl h-12" />
            </div>

            <div className="space-y-2">
              <Label>Data</Label>
              <DataBr required value={formData.date} onChange={v => setFormData({...formData, date: v})} />
              {/* Receita pra frente não é dinheiro em caixa: avisa antes de
                  salvar em vez de deixar o valor inflar o saldo do mês. A
                  segunda linha vale pra quem abriu o lápis numa parcela que
                  está em "A receber" — salvar aqui não dá baixa. */}
              {formData.type === "receita" && formData.date > localIsoDate() ? (
                <p className="text-xs text-muted-foreground">
                  Data futura: entra em <strong>A receber</strong> e só soma no saldo quando você registrar o recebimento.
                </p>
              ) : formData.type === "receita" && isEditing && formData.pending ? (
                <p className="text-xs text-muted-foreground">
                  Está em <strong>A receber</strong>. Salvar aqui não dá baixa — use o botão Receber.
                </p>
              ) : null}
            </div>

            <div className="space-y-2">
              <Label>Forma de pagamento <span className="font-normal text-muted-foreground">(opcional)</span></Label>
              <select className="w-full border rounded-md p-2 text-sm bg-background h-10" value={formData.paymentMethod} onChange={e => setFormData({...formData, paymentMethod: e.target.value})}>
                <option value="">—</option>
                {EXPENSE_PAYMENT_METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>

            {formData.type === "despesa" && (
              <div className="flex items-start justify-between gap-3 rounded-lg border bg-muted/30 p-3">
                <div className="min-w-0">
                  <Label className="text-sm font-medium">Conta a pagar (pendente)</Label>
                  <p className="text-xs text-muted-foreground mt-0.5">Só entra no caixa quando você registrar o pagamento.</p>
                  {formData.pending && (
                    <div className="mt-2 space-y-1">
                      <Label className="text-xs">Vencimento</Label>
                      <DataBr className="bg-card" value={formData.dueDate || formData.date} onChange={v => setFormData({...formData, dueDate: v})} />
                    </div>
                  )}
                </div>
                <Switch className="mt-0.5 shrink-0" checked={formData.pending} onCheckedChange={(v) => setFormData({...formData, pending: v})} />
              </div>
            )}

            <div className="space-y-2">
              <Label>Categoria</Label>
              {showNewCat ? (
                <div className="flex items-center gap-2">
                  <Input
                    value={newCatName}
                    onChange={(e) => setNewCatName(e.target.value)}
                    placeholder="Nome da nova categoria"
                    className="flex-1 ring-2 ring-primary focus-visible:ring-primary"
                    autoFocus
                    onKeyDown={(e) => { if (e.key === "Enter" && newCatName.trim()) { e.preventDefault(); createCatMutation.mutate(newCatName.trim()); } }}
                  />
                  <Button
                    type="button"
                    className="bg-primary hover:bg-primary/90 text-white shrink-0 h-10"
                    onClick={() => { if (newCatName.trim()) createCatMutation.mutate(newCatName.trim()); }}
                    disabled={!newCatName.trim() || createCatMutation.isPending}
                  >
                    {createCatMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Criar"}
                  </Button>
                  <Button
                    type="button"
                    size="icon"
                    variant="outline"
                    className="shrink-0 h-10 w-10"
                    onClick={() => { setShowNewCat(false); setNewCatName(""); }}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : editingCatId ? (
                <div className="flex items-center gap-2">
                  <Input
                    value={editCatName}
                    onChange={(e) => setEditCatName(e.target.value)}
                    placeholder="Nome da categoria"
                    className="flex-1 ring-2 ring-primary focus-visible:ring-primary"
                    autoFocus
                    onKeyDown={(e) => { if (e.key === "Enter" && editCatName.trim()) { e.preventDefault(); updateCatMutation.mutate({ id: editingCatId, name: editCatName.trim() }); } }}
                  />
                  <Button
                    type="button"
                    className="bg-primary hover:bg-primary/90 text-white shrink-0 h-10"
                    onClick={() => { if (editCatName.trim()) updateCatMutation.mutate({ id: editingCatId, name: editCatName.trim() }); }}
                    disabled={!editCatName.trim() || updateCatMutation.isPending}
                  >
                    {updateCatMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Salvar"}
                  </Button>
                  <Button
                    type="button"
                    size="icon"
                    variant="outline"
                    className="shrink-0 h-10 w-10"
                    onClick={() => { setEditingCatId(null); setEditCatName(""); }}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <select className="flex-1 min-w-0 h-10 border rounded-md p-2 text-sm bg-background" value={formData.categoryId} onChange={(e) => setFormData({ ...formData, categoryId: e.target.value })}>
                    <option value="">Selecionar categoria...</option>
                    {filteredCategories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                  {formData.categoryId ? (
                    <>
                      <Button
                        type="button"
                        size="icon"
                        variant="outline"
                        className="shrink-0 h-10 w-10 text-primary hover:text-primary hover:bg-primary/10"
                        onClick={() => {
                          const cat = filteredCategories.find(c => c.id === formData.categoryId);
                          if (cat) { setEditingCatId(cat.id); setEditCatName(cat.name); }
                        }}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        type="button"
                        size="icon"
                        variant="outline"
                        className="shrink-0 h-10 w-10 text-destructive hover:text-destructive hover:bg-destructive/10"
                        onClick={() => setDeleteCatConfirmId(formData.categoryId)}
                        disabled={deleteCatMutation.isPending}
                      >
                        {deleteCatMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                      </Button>
                    </>
                  ) : (
                    <Button
                      type="button"
                      size="icon"
                      variant="outline"
                      className="shrink-0 h-10 w-10"
                      onClick={() => setShowNewCat(true)}
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label>Descrição</Label>
              <Input required value={formData.description} onChange={e => setFormData({...formData, description: e.target.value})} placeholder="Ex: Conta de luz, Venda balcão..." />
            </div>
          </div>
          <DialogFooter className="!flex-row gap-2 justify-end">
            <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
            <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending}>{(createMutation.isPending || updateMutation.isPending) ? <Loader2 className="w-4 h-4 animate-spin"/> : "Salvar"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>

      {/* AlertDialog de confirmação pra excluir categoria. Substitui confirm()
          nativo. Renderiza por cima do Dialog (z-index do AlertDialog é maior
          por padrão no Radix). */}
      <AlertDialog open={!!deleteCatConfirmId} onOpenChange={(open) => !open && setDeleteCatConfirmId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir esta categoria?</AlertDialogTitle>
            <AlertDialogDescription>
              As transações dela ficam sem categoria; valores e datas não mudam.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (deleteCatConfirmId) deleteCatMutation.mutate(deleteCatConfirmId);
                setDeleteCatConfirmId(null);
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}
